import type { ModuleFederationRuntimePlugin } from '@module-federation/enhanced/runtime';

// A single host build must be deployable to any environment, so the MFE URLs are
// not known at build time: `npm run build` runs without the dotenv env files and
// every remote gets `undefined/remoteEntry.js` baked into its registration. The
// real URLs arrive at runtime through window.env (vite-plugin-runtime-env rewrites
// the import.meta.env reads below to window.env in production builds, and
// env-replace.js fills window.env at deploy time).
//
// Since @module-federation/vite 1.19 the remote proxies register and load remotes
// under internal names (`__mfe_internal__main-app__mf_owner__1__pricer`), so
// re-registering `pricer` from bootstrap with { force: true } no longer reaches
// them. This runtime plugin is loaded through the federation() `runtimePlugins`
// option instead — it runs inside the same runtime instance the proxies use.
//
// The rewrite happens in beforeRegisterRemote: it fires for every registration
// path (init options and registerRemotes calls) BEFORE the runtime normalizes
// non-http entries against window.location.origin — after that, the broken entry
// is an absolute `https://<host>/undefined/remoteEntry.js` and share-scope init
// fetches it without ever passing through the load-time hooks.
const REMOTE_URLS: Record<string, string | undefined> = {
  dashboard: import.meta.env.VITE_APP_DASHBOARD_MFE_URL,
  purchase: import.meta.env.VITE_APP_PURCHASE_MFE_URL,
  marketOrder: import.meta.env.VITE_APP_ORDER_MFE_URL,
  orgManagement: import.meta.env.VITE_APP_ORG_MANAGEMENT_MFE_URL,
  pricer: import.meta.env.VITE_APP_PRICER_MFE_URL,
  marketDistribution: import.meta.env.VITE_APP_MARKET_DISTRIBUTION_MFE_URL,
  seasonSeat: import.meta.env.VITE_APP_SEASON_SEAT_MFE_URL,
};

interface RemoteWithEntry {
  name: string;
  entry?: string;
}

// Remote names may be plain (`pricer`) or internally prefixed
// (`__mfe_internal__main-app__mf_owner__1__pricer`) — match on the plain suffix.
const resolveRuntimeEntry = (remoteName: string): string | undefined => {
  const plainName = Object.keys(REMOTE_URLS).find((name) => remoteName === name || remoteName.endsWith(`__${name}`));
  const url = plainName ? REMOTE_URLS[plainName] : undefined;
  return url ? `${url}/remoteEntry.js` : undefined;
};

// Only repair entries the build could not resolve — in dev the env values are
// present at config time and the baked entries are already correct. The entry may
// be the raw build-time string (`undefined/remoteEntry.js`) or, after the runtime
// normalized it, an absolute URL (`https://<host>/undefined/remoteEntry.js`).
const rewriteEntry = (remote: RemoteWithEntry): void => {
  const { entry } = remote;
  if (!entry || !(entry.startsWith('undefined/') || entry.includes('/undefined/'))) return;
  const resolved = resolveRuntimeEntry(remote.name);
  if (resolved) remote.entry = resolved;
};

const mfRuntimeEnvUrls = (): ModuleFederationRuntimePlugin => ({
  name: 'mf-runtime-env-urls',
  beforeRegisterRemote(args) {
    if ('entry' in args.remote) rewriteEntry(args.remote);
    return args;
  },
  // Safety net for any load path that resolves without a fresh registration.
  afterResolve(args) {
    const { remote, remoteInfo } = args;
    rewriteEntry(remoteInfo);
    if ('entry' in remote) rewriteEntry(remote);
    return args;
  },
});

export default mfRuntimeEnvUrls;
